import { useEffect, useState } from "react";
import styled from "styled-components";
import dayjs from "dayjs";

import {
  Box,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { DataGrid } from "@mui/x-data-grid";

import ElectricBoltOutlinedIcon from "@mui/icons-material/ElectricBoltOutlined";
import ShowChartIcon from "@mui/icons-material/ShowChart";
// import SearchIcon from "@mui/icons-material/Search";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
// import DownloadIcon from "@mui/icons-material/Download";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";

import { getSellerTokenTransaction } from "src/api/tokenTransaction";

import font from "src/styles/font";
import device from "src/styles/device";
import {
  Black,
  DarkGrey,
  Green,
  Grey,
  Primary,
  Red,
  White,
  White01,
} from "src/styles/color";
import { convertIntToCurrency } from "src/common";

import { IconToken, IconDocument } from "src/icons";

import Main from "src/components/ui/Container";
import Loading from "src/components/ui/Loading";
import TextField from "src/components/core/TextField";
import Button from "src/components/core/Button";
import Chip from "src/components/core/Chip";

const Container = styled.div`
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 24px;
`;

const Wrapper = styled.div`
  width: 100%;
  border-radius: 16px;
  border: 1px solid ${White01};
  background-color: ${White};
  box-shadow:
    0 1px 3px 0 rgba(0, 0, 0, 0.1),
    0 1px 2px -1px rgba(0, 0, 0, 0.1);
`;

const SummaryCard = styled(Wrapper)`
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SpanSummaryTitle = styled.div`
  color: ${({ color }) => (color ? color : Black)};
  font-size: ${font.size.xl};
  font-weight: 700;
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};

  @media ${device.mobileL} {
    font-size: ${font.size.md};
  }
`;

const SpanItemName = styled.div`
  color: ${({ color }) => (color ? color : Black)};
  font-size: ${font.size.md};
  font-weight: ${({ fontWeight }) => (fontWeight ? fontWeight : 500)};
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};

  @media ${device.mobileL} {
    font-size: ${font.size.sm};
  }
`;

const SpanSubItem = styled.div`
  color: ${({ color }) => (color ? color : Grey)};
  font-size: ${font.size.sm};
  font-weight: ${({ fontWeight }) => (fontWeight ? fontWeight : 400)};

  span {
    font-size: ${font.size.sm};
    font-weight: 400;
  }

  @media ${device.mobileL} {
    font-size: ${font.size.xs};
  }
`;

const WrapperFilter = styled(Wrapper)`
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media ${device.tabletL} {
    flex-wrap: wrap;
  }
`;

const WrapperDataGrid = styled(Wrapper)`
  width: 100%;
  height: 600px;

  .MuiDataGrid-root {
    border: 1px solid ${White01};
    border-radius: 16px;
    overflow: hidden;
  }
  .MuiDataGrid-columnHeaders {
    background-color: rgba(249, 250, 251, 0.5);
  }
  .MuiDataGrid-root .MuiDataGrid-columnSeparator--sideRight {
    color: #f5f5f5;
  }
  .MuiDataGrid-root .MuiDataGrid-columnHeader {
    padding: 0 16px;
  }
  .MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within {
    outline: none;
  }
  .MuiDataGrid-root .MuiDataGrid-cell {
    padding: 0 16px;
  }
  .MuiDataGrid-root .MuiDataGrid-cell:focus {
    outline: none;
    border-bottom: solid #f5f5f5 1px 0 1px 0;
  }
  .MuiDataGrid-root .MuiDataGrid-row.Mui-selected {
    background-color: #ffffff;
  }
`;

const ImageNoData = styled.image`
  width: 120px;
  height: 120px;
  background-image: url("https://xsurface-images.s3.ap-southeast-1.amazonaws.com/no+data.svg");
  background-repeat: no-repeat;
  background-size: 100%;
`;
const ImageNotFound = styled.image`
  width: 120px;
  height: 120px;
  background-image: url("https://xsurface-images.s3.ap-southeast-1.amazonaws.com/not+found.svg");
  background-repeat: no-repeat;
  background-size: 100%;
`;

const TextNoData = styled.pre`
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
`;

const SellerTokenTransaction = ({ open }) => {
  const [isLoading, setIsLoading] = useState(false);
  const defaultSearch = {
    keyword: "",
    page: 0,
    page_size: 10,
    type: "", // ["TOPUP", "USAGE", "REFUND", "ADJUST", "EXPIRED"]
    start_date: null,
    end_date: null,
  };
  const [dataSearch, setDataSearch] = useState(defaultSearch);
  const [tokenTransactionList, setTokenTransactionList] = useState([]);
  const [tokenSummary, setTokenSummary] = useState({
    totalTopupAmount: 0,
    totalUsedAmount: 0,
    currentBalance: 0,
    expiringAmount: 0,
  });
  const [rowCount, setRowCount] = useState(0);

  const fetchBillingList = async () => {
    try {
      setIsLoading(true);

      const { transactions, summary, pagination } =
        await getSellerTokenTransaction({
          ...dataSearch,
          page: dataSearch.page,
          start_date: dataSearch?.start_date ? dataSearch?.start_date : null,
          end_date: dataSearch?.end_date ? dataSearch?.end_date : null,
        });

      setTokenTransactionList(transactions);
      setTokenSummary(summary);
      setRowCount(pagination.total);
    } catch (error) {
      console.error("Error fetching billing list:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBillingList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataSearch.page,
    dataSearch.type,
    dataSearch.start_date,
    dataSearch.end_date,
  ]);

  const columns = [
    {
      field: "createdAt",
      headerName: "วัน / เวลา",
      flex: 0.8,
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params, index) => {
        return (
          <Box
            sx={{
              width: "100%",
              margin: "16px 0",
              outline: "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <SpanItemName mb={8}>
              {params.row.createdAt
                ? dayjs(params.row.createdAt).format("DD/MM/YYYY")
                : "-"}
            </SpanItemName>

            <Stack direction="row" alignItems="center" spacing={1}>
              <SpanSubItem>
                {params.row.createdAt
                  ? dayjs(params.row.createdAt).format("HH:mm")
                  : "-"}
              </SpanSubItem>
            </Stack>
          </Box>
        );
      },
    },
    {
      field: "transactionInfo",
      headerName: "รายการ",
      flex: 1.5,
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params, index) => {
        return (
          <Box
            sx={{
              width: "100%",
              margin: "16px 0",
              outline: "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <SpanItemName mb={8}>{params.row?.note || "-"}</SpanItemName>

            <Stack direction="row" alignItems="center" spacing={1}>
              <SpanSubItem>
                {params?.row?.order ? params?.row?.order?.order_no || "-" : `-`}
              </SpanSubItem>
            </Stack>
          </Box>
        );
      },
    },
    {
      field: "type",
      headerName: "ประเภท",
      flex: 1,
      headerAlign: "left",
      align: "left",
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Box>
            <Chip
              variant="secondary"
              {...(params.row?.type === "TOPUP"
                ? {
                    icon: <CallReceivedIcon />,
                    label: "เติมเงิน",
                    color: "success",
                  }
                : params.row?.type === "USAGE"
                  ? {
                      icon: <CallMadeIcon />,
                      label: "ใช้งาน",
                      color: "warning",
                    }
                  : params.row?.type === "REFUND"
                    ? {
                        icon: <CurrencyExchangeIcon />,
                        label: "คืนเครดิต (Refund)",
                        color: "info",
                      }
                    : params.row?.type === "ADJUST"
                      ? {
                          icon: <VerifiedUserOutlinedIcon />,
                          label: "Admin เพิ่มให้",
                          color: "secondary",
                        }
                      : params.row?.type === "EXPIRED"
                        ? {
                            icon: <AutorenewIcon />,
                            label: "หมดอายุ",
                            color: "error",
                          }
                        : {
                            icon: <AutorenewIcon />,
                            label: "ไม่ทราบ",
                            color: "default",
                          })}
            />
          </Box>
        );
      },
    },
    {
      field: "amount",
      headerName: "จำนวนเครดิต",
      flex: 0.8,
      headerAlign: "center",
      align: "center",
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Box sx={{ textAlign: "center" }}>
            {params.row?.type === "TOPUP" ||
            params.row?.type === "ADJUST" ||
            params.row?.type === "REFUND" ? (
              <SpanItemName fontWeight={600} color={Green}>
                +{convertIntToCurrency(params?.row?.amount, false)}
              </SpanItemName>
            ) : params.row?.type === "USAGE" ||
              params.row?.type === "EXPIRED" ? (
              <SpanItemName fontWeight={600} color={Red}>
                {convertIntToCurrency(params?.row?.amount, false)}
              </SpanItemName>
            ) : (
              <SpanItemName fontWeight={600}>
                {convertIntToCurrency(params?.row?.amount, false)}
              </SpanItemName>
            )}
          </Box>
        );
      },
    },
    {
      field: "balance_after",
      headerName: "คงเหลือ",
      flex: 0.8,
      headerAlign: "center",
      align: "center",
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Box sx={{ textAlign: "center" }}>
            <SpanItemName fontWeight={600}>
              {convertIntToCurrency(params?.row?.balance_after, false)}
            </SpanItemName>
          </Box>
        );
      },
    },
    // {
    //   field: "status",
    //   headerName: "หมดอายุ",
    //   flex: 1,
    //   headerAlign: "center",
    //   align: "center",
    //   editable: false,
    //   sortable: false,
    //   disableColumnMenu: true,
    //   renderCell: (params) => {
    //     return (
    //       <Chip
    //         icon={
    //           params.row?.status === "PAID" ? (
    //             <CheckCircleOutlinedIcon />
    //           ) : params.row?.status === "PENDING" ? (
    //             <AccessTimeIcon />
    //           ) : (
    //             <HighlightOffIcon />
    //           )
    //         }
    //         label={
    //           params.row?.status === "PAID"
    //             ? "ชำระแล้ว"
    //             : params.row?.status === "PENDING"
    //               ? "รอชำระ"
    //               : params.row?.status === "CANCELLED"
    //                 ? "ยกเลิก"
    //                 : params.row?.status === "EXPIRED"
    //                   ? "หมดอายุ"
    //                   : "ไม่สำเร็จ"
    //         }
    //         variant="secondary"
    //         color={
    //           params.row?.status === "PAID"
    //             ? "success"
    //             : params.row?.status === "PENDING"
    //               ? "warning"
    //               : "error"
    //         }
    //       />
    //     );
    //   },
    // },
  ];

  const handlePageChange = (newPage) => {
    setDataSearch({ ...dataSearch, page: newPage });
  };

  function NoRowsOverlay() {
    return (
      <Stack height="100%" alignItems="center" justifyContent="center">
        <ImageNoData></ImageNoData>
        <TextNoData>No Data</TextNoData>
      </Stack>
    );
  }

  function NoResultsOverlay() {
    return (
      <Stack height="100%" alignItems="center" justifyContent="center">
        <ImageNotFound></ImageNotFound>
        <TextNoData>Not Found</TextNoData>
      </Stack>
    );
  }

  return (
    <>
      <Main open={open}>
        <Container>
          <Grid container columnSpacing={2} rowSpacing={3}>
            {/* Summary Transacrion */}
            <Grid item xs={12} container spacing={2}>
              <Grid item xs={6} md={3}>
                <SummaryCard>
                  <Box
                    sx={{
                      p: 1.5,
                      background: "#EFF6FF",
                      borderRadius: 4,
                      height: 48,
                    }}
                  >
                    <IconToken width={24} height={24} fill="#2B7FFF" />
                  </Box>

                  <Box>
                    <SpanItemName color={DarkGrey}>ยอดเติมสะสม</SpanItemName>
                    <SpanSummaryTitle>
                      {convertIntToCurrency(
                        tokenSummary?.totalTopupAmount,
                        false,
                      )}
                    </SpanSummaryTitle>
                    <SpanSubItem>เครดิตทั้งหมด</SpanSubItem>
                  </Box>
                </SummaryCard>
              </Grid>

              <Grid item xs={6} md={3}>
                <SummaryCard>
                  <Box
                    sx={{
                      p: 1.5,
                      background: "#FEF2F2",
                      borderRadius: 4,
                      height: 48,
                    }}
                  >
                    <ElectricBoltOutlinedIcon sx={{ color: "#FB2C36" }} />
                  </Box>

                  <Box>
                    <SpanItemName color={DarkGrey}>ยอดที่ใช้ไป</SpanItemName>
                    <SpanSummaryTitle>
                      {convertIntToCurrency(
                        tokenSummary?.totalUsedAmount,
                        false,
                      )}
                    </SpanSummaryTitle>
                    <SpanSubItem>เครดิตที่ใช้เจนภาพ</SpanSubItem>
                  </Box>
                </SummaryCard>
              </Grid>

              <Grid item xs={6} md={3}>
                <SummaryCard>
                  <Box
                    sx={{
                      p: 1.5,
                      background: "#F0FDF4",
                      borderRadius: 4,
                      height: 48,
                    }}
                  >
                    <ShowChartIcon sx={{ color: "#00C950" }} />
                  </Box>

                  <Box>
                    <SpanItemName color={DarkGrey}>ยอดคงเหลือ</SpanItemName>
                    <SpanSummaryTitle>
                      {convertIntToCurrency(
                        tokenSummary?.currentBalance,
                        false,
                      )}
                    </SpanSummaryTitle>
                    <SpanSubItem>เครดิตพร้อมใช้งาน</SpanSubItem>
                  </Box>
                </SummaryCard>
              </Grid>

              <Grid item xs={6} md={3}>
                <SummaryCard>
                  <Box
                    sx={{
                      p: 1.5,
                      background: "#FAF5FF",
                      borderRadius: 4,
                      height: 48,
                    }}
                  >
                    <IconDocument width={24} height={24} fill="#AD46FF" />
                  </Box>

                  <Box>
                    <SpanItemName color={DarkGrey}>ใกล้หมดอายุ</SpanItemName>
                    <SpanSummaryTitle>
                      {convertIntToCurrency(
                        tokenSummary?.expiringAmount,
                        false,
                      )}
                    </SpanSummaryTitle>
                    <SpanSubItem>ภายใน 30 วัน</SpanSubItem>
                  </Box>
                </SummaryCard>
              </Grid>
            </Grid>

            {/* Search & Filter */}
            <Grid item xs={12}>
              <WrapperFilter>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}
                  flexWrap={{ xs: "wrap", md: "unset" }}
                >
                  {/* <TextField
                    placeholder="ค้นหารายการ..."
                    sx={{ width: { xs: "100%", sm: 300 } }}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          id="button-search-transaction"
                          aria-label="search"
                          sx={{ padding: 0 }}
                          onClick={() => {}}
                        >
                          <SearchIcon
                            sx={{
                              color: "#BF0013",
                              cursor: "pointer",
                            }}
                          />
                        </IconButton>
                      ),
                    }}
                  /> */}

                  <FormControl
                    sx={{
                      minWidth: 160,
                      "& .MuiInputBase-input": {
                        padding: "12px 16px",
                      },
                      "& .MuiInputLabel-root": {
                        transform: "translate(14px, 12px) scale(1)",
                      },
                      "& .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-root.MuiInputLabel-shrink":
                        {
                          transform: "translate(14px, -9px) scale(0.75)",
                        },
                    }}
                  >
                    <InputLabel id="usage-type-select-label">
                      ประเภทการใช้งาน
                    </InputLabel>
                    <Select
                      labelId="usage-type-select-label"
                      id="usage-type-select"
                      label="ประเภทการใช้งาน"
                      value={dataSearch.type}
                      onChange={(e) =>
                        setDataSearch({
                          ...dataSearch,
                          type: e.target.value,
                          page: 0,
                        })
                      }
                    >
                      <MenuItem value={""}>ทุกประเภท</MenuItem>
                      <MenuItem value={"TOPUP"}>เติมเงิน</MenuItem>
                      <MenuItem value={"USAGE"}>ใช้งาน</MenuItem>
                      <MenuItem value={"REFUND"}>คืนเครดิต (Refund)</MenuItem>
                      <MenuItem value={"ADJUST"}>Admin เพิ่มให้</MenuItem>
                      <MenuItem value={"EXPIRED"}>หมดอายุ</MenuItem>
                    </Select>
                  </FormControl>

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="start date"
                      placeholder="ระบุวันที่"
                      inputFormat="DD/MM/YYYY"
                      value={dataSearch?.start_date}
                      onChange={(newValue) =>
                        setDataSearch({
                          ...dataSearch,
                          start_date: newValue.$d,
                          page: 0,
                        })
                      }
                      maxDate={
                        dataSearch?.end_date ? dataSearch?.end_date : null
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          inputProps={{ ...params.inputProps, readOnly: true }}
                          variant="outlined"
                          sx={{
                            maxWidth: 180,
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="end date"
                      placeholder="ระบุวันที่"
                      inputFormat="DD/MM/YYYY"
                      value={dataSearch?.end_date}
                      onChange={(newValue) =>
                        setDataSearch({
                          ...dataSearch,
                          end_date: newValue.$d,
                          page: 0,
                        })
                      }
                      minDate={
                        dataSearch?.start_date ? dataSearch?.start_date : null
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          inputProps={{ ...params.inputProps, readOnly: true }}
                          variant="outlined"
                          sx={{
                            maxWidth: 180,
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>

                  <Button
                    variant="text"
                    startIcon={<RefreshOutlinedIcon />}
                    onClick={() => setDataSearch(defaultSearch)}
                  >
                    Clear Filter
                  </Button>
                </Box>

                <Box
                  sx={{
                    width: "max-content",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {/* <Button variant="contained" startIcon={<DownloadIcon />}>
                    Export CSV
                  </Button> */}

                  <IconButton
                    id="button-refresh-transaction"
                    aria-label="refresh"
                    title="refresh"
                    sx={{ padding: 0 }}
                    onClick={() => fetchBillingList()}
                  >
                    <RefreshOutlinedIcon
                      sx={{
                        color: Primary,
                        cursor: "pointer",
                      }}
                    />
                  </IconButton>
                </Box>
              </WrapperFilter>
            </Grid>

            {/* package detail table */}
            <Grid item xs={12}>
              <WrapperDataGrid>
                <DataGrid
                  rows={tokenTransactionList}
                  getRowId={(row) => row._id}
                  getRowHeight={() => "auto"}
                  loading={isLoading}
                  columns={columns}
                  pageSize={10}
                  rowCount={rowCount}
                  paginationMode="server"
                  page={dataSearch.page}
                  onPageChange={(e) => handlePageChange(e)}
                  pagination
                  disableSelectionOnClick
                  components={{
                    NoRowsOverlay,
                    NoResultsOverlay,
                  }}
                  componentsProps={{
                    basePopper: {},
                  }}
                />
              </WrapperDataGrid>
            </Grid>
          </Grid>
        </Container>
      </Main>

      {isLoading && <Loading />}
    </>
  );
};

export default SellerTokenTransaction;
